<template>
  <q-table
    flat
    dense
    title="Treats"
    v-if="items"
    :rows="items"
    row-key="title"
    separator="cell"
    :no-data-label="$t('messages.no_data_available')"
    hide-bottom
    class="text-grey-8 transparent-table"
    :rows-per-page-options="[0]"
  >
    <template v-slot:header>
      <q-tr>
        <q-th key="action" class="text-center" style="width: 50px">
          <span class="text-bold">配信</span>
        </q-th>
        <q-th class="text-center" style="width: 10px">|</q-th>
        <q-th style="width: 150px"></q-th>
        <q-th key="popup_name" class="text-left">
          <span class="text-bold">{{ $t('label.popup.popup_name') }}</span>
        </q-th>
        <q-th key="total_display">
          <span class="text-bold">{{ $t('label.total_display') }}</span>
        </q-th>
        <q-th key="click_rate">
          <span class="text-bold">{{ $t('label.click_rate') }}</span>
        </q-th>
        <q-th key="total_click">
          <span class="text-bold">{{ $t('label.total_click') }}</span>
        </q-th>
      </q-tr>
    </template>
    <template v-slot:body="props">
      <q-tr
        :props="props"
        :index="props.rowKey"
        @click="selectedPopup = props.row"
        :class="props.row._id == selectedPopup._id ? 'row-selected' : ''"
      >
        <q-td v-if="!isTestMode">
          <q-toggle
            round
            v-model="props.row.is_active"
            val="props.row.is_active"
            @click="handleToggleActive(props.row)"
          />
        </q-td>
        <q-td v-else>
          <q-toggle
            round
            v-model="props.row.is_test_mode"
            val="props.row.is_test_mode"
            @click="handleToggleActive(props.row)"
          />
        </q-td>
        <q-td key="popup_name" :props="props">|</q-td>
        <q-td>
          <div class="row items-center" style="width: 150px">
            <div
              :style="{
                backgroundImage: `url(${props.row.images[0].pc.img_thumb})`,
                backgroundSize: 'cover',
              }"
              style="width: 100px; height: 100px"
              class="q-ml-sm square radius-4 img-content"
              @click="onMediaDetails(props.row)"
            ></div>
            <q-btn size="sm" flat round icon="edit" @click="onEdit(props.row)" class="q-ml-sm" />
          </div>
        </q-td>
        <q-td key="popup_name" :props="props">
          <span class="cursor-pointer">{{ props.row.popup_name }}</span
          ><br />
          <a
            :href="getDetailsUrl(props.row)"
            @click="onDetails($event, props.row)"
            class="cursor-pointer hyper-link-chart"
          >
            {{ $t('label.data_transition') }}
          </a>
          <span>/{{ props.row._id.length > 4 ? props.row._id.slice(0, 4) + '***' : props.row._id }}</span>
        </q-td>
        <q-td key="total_display" :props="props">
          {{ numberWithCommas(props.row.total_display) }}
        </q-td>
        <q-td key="click_rate" :props="props"> {{ roundNumber(props.row.click_rate) }}% </q-td>
        <q-td key="total_click" :props="props">
          {{ numberWithCommas(props.row.total_click) }}
        </q-td>
      </q-tr>
    </template>
    <template v-slot:top>
      <h2 class="text-h6 text-black">
        ポップアップ一覧 <span class="q-ml-md">(配信中: {{ activePopupCount }} / {{ maxActivePopup }})</span>
      </h2>
      <q-space />
      <DateRangeSelectorV2
        v-model="dateRange"
        :disable="false"
        :opens="'right'"
        @update:onChangeDateRange="changeDateRange"
      ></DateRangeSelectorV2>
      <q-btn class="q-ml-sm btn-save" no-caps color="primary" @click="onAdd" label="ポップアップ追加" />
    </template>
  </q-table>
  <PopupModal
    v-if="popupModalVisible"
    :modalVisible="popupModalVisible"
    :popupData="selectedPopup"
    @update:closeModal="onCloseModal"
    @update:addPopup="onAddPopup"
    @update:updatePopup="onUpdatePopup"
  />
  <MediaDetailsModal
    v-if="modalMediaVisible"
    :modalVisible="modalMediaVisible"
    :mediaUrl="selectedPopup.images[0].pc.img_org"
    :mediaName="selectedPopup.images[0].pc.filename"
    @update:closeModal="modalMediaVisible = false"
  ></MediaDetailsModal>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import { maska } from 'maska'
import { IDateRangePicker, IPopup } from '@/utils/types'
import { Formater } from '@/utils/formater'
import { ACTION_POPUP } from '@/store/actions'
import { DatetimeUtils, lastNMonth, now } from '@/utils/datetime'
import DateRangeSelectorV2 from '@/components/common/ui/DateRangeSelectorV2.vue'
import PopupModal from '@/components/story/common/PopupModal.vue'
import MessagesFormModal from '@/components/messages/MessagesFormModal.vue'
import { constant } from '@/utils/constants'
import MediaDetailsModal from '@/components/media-manager/MediaDetailsModal.vue'
import cloneDeep from 'lodash/cloneDeep'

@Options({
  components: { MediaDetailsModal, MessagesFormModal, PopupModal, DateRangeSelectorV2 },
  directives: { maska },
  emits: [
    'update:modelValue',
    'update:addPopup',
    'update:updatePopup',
    'update:activePopup',
    'update:isSettingValue',
    'update:selectedPopup',
  ],
})
export default class PopupListView extends Vue {
  @Prop()
  popupList!: IPopup[]

  @Prop({})
  isTestMode!: boolean

  @Prop()
  isSetting!: boolean

  @Prop()
  appId!: string

  @Prop()
  campaignId!: string

  items: IPopup[] = []
  popupModalVisible = false
  modalMediaVisible = false
  maxActivePopup = constant.STORY_POPUP.MAX_ACTIVATED

  dateRange: IDateRangePicker = {
    startDate: '',
    endDate: '',
  }

  blankPopup: IPopup = {
    _id: '',
    app_id: '',
    campaign_id: '',
    is_active: false,
    is_test_mode: false,
    display_id: '',
    popup_name: '',
    images: [],
    animation_type: constant.animation_type.fade_in,
  }

  selectedPopup: IPopup = cloneDeep(this.blankPopup)

  get setting() {
    return this.isSetting
  }

  set setting(value: boolean) {
    this.$emit('update:isSettingValue', value)
  }

  get activePopupCount() {
    if (this.isTestMode) {
      return this.items.filter((item) => item.is_test_mode === true).length
    }
    return this.items.filter((item) => item.is_active === true).length
  }

  @Watch('isTestMode', { immediate: true })
  @Watch('popupList', { deep: true })
  async loadData() {
    if (this.appId && this.campaignId) {
      this.items = await this.$store.dispatch(ACTION_POPUP.LOAD_ITEMS, {
        app_id: this.appId,
        campaign_id: this.campaignId,
        is_test_mode: this.isTestMode,
        date_from: DatetimeUtils.formatDate(this.dateRange.startDate),
        date_to: DatetimeUtils.formatDate(this.dateRange.endDate),
      })
    }
  }

  @Watch('selectedPopup', { immediate: true })
  onSelectedPopup(data: IPopup) {
    this.$emit('update:selectedPopup', data)
  }

  onDetails(event, data: IPopup) {
    // Check if Ctrl (Windows) or Meta (Mac) is pressed
    if (event.ctrlKey || event.metaKey) {
      // Let the browser handle opening in a new tab
      return
    }
    // Otherwise, navigate programmatically
    event.preventDefault()

    this.goto('popup_details', {
      app_id: this.appId,
      campaign_id: data.campaign_id,
      popup_id: data._id,
      from_date: lastNMonth(1),
      to_date: now(),
    })
  }

  getDetailsUrl(data) {
    // Construct the URL as a string for direct navigation
    return this.$router.resolve({
      name: 'popup_details',
      params: {
        app_id: this.appId as string,
        campaign_id: data.campaign_id,
        popup_id: data._id,
        from_date: lastNMonth(1),
        to_date: now(),
      },
    }).href
  }

  onMediaDetails(data: IPopup) {
    this.selectedPopup = data
    this.modalMediaVisible = true
  }

  goto(name: string, params = {}) {
    this.$router.push({
      name,
      params,
    })
  }

  async handleToggleActive(record: IPopup) {
    if (this.activePopupCount > this.maxActivePopup && this.statusMode(record)) {
      if (this.isTestMode) {
        record.is_test_mode = false
      } else {
        record.is_active = false
      }
      this.$q.dialog({
        title: this.$t('messages.confirm_please_note'),
        message: this.$t('messages.please_diasble_popup_story'),
        cancel: {
          flat: true,
          label: this.$t('cancel'),
        },
        persistent: true,
        html: true,
      })
      return
    }
    this.$emit('update:activePopup', record)
  }

  statusMode(item: IPopup) {
    if (this.isTestMode) {
      return item.is_test_mode ?? false
    } else {
      return item.is_active ?? false
    }
  }

  changeDateRange(value) {
    this.dateRange = value
    this.loadData()
  }

  onAdd() {
    this.selectedPopup = cloneDeep(this.blankPopup)
    this.popupModalVisible = true
    this.setting = true
  }

  onEdit(data: IPopup) {
    this.selectedPopup = data
    this.popupModalVisible = true
  }

  onAddPopup(data: IPopup) {
    this.$emit('update:addPopup', data)
  }

  onUpdatePopup(data: IPopup) {
    this.$emit('update:updatePopup', data)
  }

  onCloseModal() {
    this.popupModalVisible = false
  }

  numberWithCommas(num) {
    return Formater.numberFormat(num)
  }

  roundNumber(num) {
    return Formater.round(num)
  }

  mounted() {
    this.dateRange = DatetimeUtils.getDateRange('last30Days')
    this.loadData()
  }
}
</script>

<style scoped lang="scss">
.transparent-table {
  background-color: transparent !important;
}
.q-table--dense .q-table th:first-child,
.q-table--dense .q-table td:first-child {
  padding-left: 8px;
}
.row-selected .fit.square.radius-4 {
  border-radius: 0;
}
</style>

function Watch(arg0: string, arg1: { immediate: boolean }) {
  throw new Error('Function not implemented.')
}
